import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase/config';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,

  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  LinearProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';

import PatientForm from '../patients/PatientForm';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonIcon from '@mui/icons-material/Person';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import { createNewEncounter } from '../../redux/slices/encountersSlice';

import AllergySelectorDialog from '../patients/AllergySelectorDialog';
import { getFacilityUsers } from '../../services/userService';
import { ADMISSION_TYPES } from '../../types/enums';
import { generateFIN } from '../../utils/finUtils';
import { useFacilityChangeListener } from '../../hooks/useFacilityChangeListener';
import { useNavigate } from 'react-router-dom';
import { getFacilityDepartments, FacilityService } from '../../services/facilityService';
import { getPatientsByFacility } from '../../services/appointmentService';
import { createPatientDocument, updatePatient } from '../../services/patientService';
import { useUserData } from '../../hooks/useUserData';
import { DatePicker } from '@mui/x-date-pickers';
import { getAuthToken } from '../../utils/authUtils';
import OncologyEpisodeSelector from '../encounters/OncologyEpisodeSelector';
import { linkEncounterToEpisode } from '../../services/episodeOfCareService';

const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(1.5),
  borderRadius: '8px',
  boxShadow: '0 4px 16px rgba(102, 126, 234, 0.2)',
}));

const ModernCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
  marginBottom: theme.spacing(2),
}));

const CompactFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiInputLabel-root': {
    fontSize: '0.875rem',
  },
  '& .MuiSelect-select': {
    paddingTop: '8px',
    paddingBottom: '8px',
  },
}));

const CompactTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputLabel-root': {
    fontSize: '0.875rem',
  },
  '& .MuiInputBase-root': {
    '& input': {
      paddingTop: '8px',
      paddingBottom: '8px',
    },
  },
}));

export default function PatientAdmission() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
 
  
  // Use the new useUserData hook for consistent data structure
  const { user, defaultFacility } = useUserData();
  
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedFacility, setSelectedFacility] = useState('');
  const [admissionDate, setAdmissionDate] = useState(new Date().toISOString().split('T')[0]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  
  // Patient type selection state
  const [patientType, setPatientType] = useState('existing'); // 'existing' or 'new'
  
  // New state for facility patients table
  const [facilityPatients, setFacilityPatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [patientsError, setPatientsError] = useState(null);

  // New state for expanded admission form
  const [admissionType, setAdmissionType] = useState('');
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState('');
  const [physicians, setPhysicians] = useState([]);
  const [selectedPhysician, setSelectedPhysician] = useState('');
  const [admittingDiagnosis, setAdmittingDiagnosis] = useState('');
  const [admissionNotes, setAdmissionNotes] = useState('');
  const [allergies, setAllergies] = useState([]);
  const [allergiesModalOpen, setAllergiesModalOpen] = useState(false);

  // Document upload state
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  // Oncology episode linking state
  const [selectedOncologyEpisode, setSelectedOncologyEpisode] = useState('');

  // Patient creation state
  const [isCreatingPatient, setIsCreatingPatient] = useState(false);
 


  // Set default facility when user facilities are loaded
  useEffect(() => {
    // First check if we already have a selected facility
    if (selectedFacility) {
      return;
    }

    // Set default facility if available
    if (defaultFacility?.id) {
      setSelectedFacility(defaultFacility.id);
    }
  }, [selectedFacility, defaultFacility]);

  // Fetch departments for selected facility
  useEffect(() => {
    const fetchDepartments = async () => {
      // Clear existing data
      setDepartments([]);
      setSelectedDepartment('');
      setRooms([]);
      setSelectedRoom('');

      // Only fetch if we have a selected facility
      if (!selectedFacility) {
        return;
      }

      try {
        console.log('Fetching departments for facility:', selectedFacility);
        const response = await getFacilityDepartments(selectedFacility);
        
        if (Array.isArray(response) && response.length > 0) {
          console.log(`Found ${response.length} departments:`, response);
          setDepartments(response);
        } else {
          console.log('No departments found for facility');
          setDepartments([]);
        }
      } catch (err) {
        console.error('Error fetching departments:', err);
        setDepartments([]);
      }
    };

    fetchDepartments();
  }, [selectedFacility]); // Only depend on selectedFacility

  // Fetch rooms for selected department
  useEffect(() => {
    const fetchRooms = async () => {
      // Clear existing room selection
      setRooms([]);
      setSelectedRoom('');

      // Only fetch if we have both facility and department selected
      if (!selectedDepartment || !selectedFacility) {
        return;
      }

      try {
        console.log(`Fetching rooms for facility ${selectedFacility} and department ${selectedDepartment}`);
        const response = await FacilityService.getFacilityRooms(selectedFacility, selectedDepartment);
        
        if (Array.isArray(response) && response.length > 0) {
          console.log(`Found ${response.length} rooms:`, response);
          setRooms(response);
        } else {
          console.log('No rooms found for selected department');
          setRooms([]);
        }
      } catch (err) {
        console.error('Error fetching rooms:', err);
        setRooms([]);
      }
    };

    fetchRooms();
  }, [selectedDepartment, selectedFacility]); // Depend on both facility and department

  // Fetch physicians for selected facility
  useEffect(() => {
    const fetchPhysicians = async () => {
      // Clear existing selections
      setPhysicians([]);
      setSelectedPhysician('');

      // Only fetch if we have a selected facility
      if (!selectedFacility) {
        return;
      }

      try {
        console.log('Fetching physicians for facility:', selectedFacility);
        const facilityUsers = await getFacilityUsers(selectedFacility);
        
        if (!facilityUsers || facilityUsers.length === 0) {
          console.log('No users found for facility');
          return;
        }

        // Filter for doctors from the facility users
        const doctors = facilityUsers.filter(u => 
          (u.roles || [u.role]).includes('doctor')
        );

        if (doctors.length > 0) {
          console.log(`Found ${doctors.length} physicians:`, doctors);
          setPhysicians(doctors);
        } else {
          console.log('No physicians found in facility users');
        }
      } catch (err) {
        console.error('Error fetching physicians for facility:', err);
      }
    };

    fetchPhysicians();
  }, [selectedFacility]); // Only depend on selectedFacility

  // Allergies modal handlers
  const handleOpenAllergiesModal = () => setAllergiesModalOpen(true);
  const handleCloseAllergiesModal = () => setAllergiesModalOpen(false);


  // Document upload handlers
  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setIsUploading(true);
    setError(null);
    setSuccess(false);

    try {
      const uploadPromises = files.map(async (file) => {
        const fileName = `${Date.now()}_${file.name}`;
        const storageRef = ref(storage, `patient-documents/${fileName}`);
        
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        
        return {
          id: fileName,
          name: file.name,
          url: downloadURL,
          size: file.size,
          type: file.type,
          uploadedAt: new Date(),
          uploadedBy: user?.uid,
          patientId: selectedPatient?.id || null
        };
      });

      const uploadedFiles = await Promise.all(uploadPromises);
      
      // Add to uploaded documents list
      setUploadedDocuments(prev => [...prev, ...uploadedFiles]);
      
      // Store document metadata using the service (only if patient is selected)
      if (selectedPatient?.id) {
        for (const fileData of uploadedFiles) {
                  await createPatientDocument({
          patientId: selectedPatient.id,
            name: fileData.name,
            description: '',
            documentType: 'admission',
            tags: ['admission'],
            fileUrl: fileData.url,
            fileSize: fileData.size,
            mimeType: fileData.type
          });
        }
      }

      setSuccess(`Successfully uploaded ${uploadedFiles.length} document(s).`);
    } catch (error) {
      console.error('Error uploading files:', error);
      setError(`Failed to upload documents: ${error.message}`);
    } finally {
      setIsUploading(false);
      // Clear the input
      event.target.value = '';
    }
  };

  const handleUploadClick = () => {
    document.getElementById('document-upload-input').click();
  };

  const handleRemoveDocument = (documentId) => {
    setUploadedDocuments(prev => prev.filter(doc => doc.id !== documentId));
  };

  // Handle new patient creation
  const handleNewPatientCreated = (newPatient) => {
    setIsCreatingPatient(false);
    setSelectedPatient(newPatient);
    setSelectedOncologyEpisode(''); // Reset oncology episode when patient changes
    setSuccess(`New patient ${newPatient.name} created and selected for admission.`);
  };

  // Handle patient creation start
  const handlePatientCreationStart = () => {
    setIsCreatingPatient(true);
    setError(null);
    setSuccess(false);
  };

  // Handle patient creation error
  const handlePatientCreationError = (error) => {
    setIsCreatingPatient(false);
    setError('Failed to create patient. Please try again.');
    console.error('Patient creation error:', error);
  };

  // Handle patient type change
  const handlePatientTypeChange = (event) => {
    const newType = event.target.value;
    setPatientType(newType);
    
    // Clear selected patient when switching types
    setSelectedPatient(null);
    setSelectedOncologyEpisode(''); // Reset oncology episode when patient changes
    
    // Clear any existing messages
    setError(null);
    setSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPatient) {
      setError('Please select a patient.');
      return;
    }
    if (!selectedFacility) {
      setError('Please select a facility.');
      return;
    }
    if (!admissionType || !selectedDepartment || !selectedRoom || !selectedPhysician) {
      setError('Please fill all required fields.');
      return;
    }
    
    // Check if patient is already admitted to this facility
    if (selectedPatient.isInpatient && selectedPatient.facilityId === selectedFacility) {
      setError(`Patient ${selectedPatient.name} is already admitted to this facility. Please discharge them first before attempting a new admission.`);
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    setSuccess(false);
    try {
      const token = await getAuthToken();
      
      // Generate unique FIN number for this encounter
      const finNumber = generateFIN();
      console.log('Generated FIN for encounter:', finNumber);
      
      const encounterData = {
        patientId: selectedPatient.id,
        type: 'inpatient',
        admissionTime: new Date(admissionDate),
        status: 'in-progress',
        facilityId: selectedFacility, // Use facilityId instead of locationId to match API expectations
        fin: finNumber,
        admissionDetails: {
          admitSource: admissionType,
          department: selectedDepartment,
          room: selectedRoom,
          attendingPhysician: selectedPhysician,
          admittingDiagnosis,
          allergies,
          admissionNotes,
          uploadedDocuments: uploadedDocuments.map(doc => ({
            id: doc.id,
            name: doc.name,
            url: doc.url,
            size: doc.size,
            type: doc.type,
            uploadedAt: doc.uploadedAt
          })),
        },
        token,
      };
      const createdEncounter = await dispatch(createNewEncounter(encounterData)).unwrap();
      const encounterId = createdEncounter.id;

      // Link to oncology episode if one was selected
      if (selectedOncologyEpisode && encounterId) {
        try {
          await linkEncounterToEpisode(selectedOncologyEpisode, encounterId);
          console.log('Successfully linked encounter to oncology episode:', selectedOncologyEpisode);
        } catch (linkError) {
          console.error('Failed to link encounter to oncology episode:', linkError);
          // Don't fail the admission if episode linking fails
        }
      }

      // Update patient information using the service
              await updatePatient(selectedPatient.id, {
        admissionStatus: 'admitted',
        currentEncounterId: encounterId,
        isInpatient: true,
        facilityId: selectedFacility,
        admittedAt: new Date(),
        allergies,
      });

      setSuccess(`Patient ${selectedPatient.name} admitted successfully to ${(selectedFacility)?.name || 'selected facility'}.`);
      setSelectedPatient(null);
      setAdmissionType('');
      setSelectedDepartment('');
      setSelectedRoom('');
      setSelectedPhysician('');
      setAdmittingDiagnosis('');
      setAdmissionNotes('');
      setAllergies([]);
      setUploadedDocuments([]);
      setSelectedOncologyEpisode('');
    } catch (err) {
      console.error('Error admitting patient:', err);
      setError('Failed to admit patient. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fetch patients for the selected facility
  useEffect(() => {
    const fetchFacilityPatients = async () => {
      // Only fetch if we have a selected facility and we're in existing patient mode
      if (!selectedFacility || patientType !== 'existing') {
        setFacilityPatients([]);
        return;
      }

      try {
        setLoadingPatients(true);
        setPatientsError(null);

        const response = await getPatientsByFacility(selectedFacility);
        
        // Map response data to display format
        const patientList = response.map(patient => ({
          id: patient.id,
          name: patient.name || 'Unknown',
          dateOfBirth: patient.dateOfBirth ? new Date(patient.dateOfBirth).toLocaleDateString() : 'N/A',
          contact: patient.contact || 'N/A',
          email: patient.email || 'N/A',
          bloodType: patient.bloodType || 'N/A',
          mrn: patient.mrn || 'N/A',
          isInpatient: patient.isInpatient || false,
          admissionStatus: patient.admissionStatus || 'not-admitted',
          facilityId: patient.facilityId // Include current facility ID for admission validation
        }));

        console.log(`Fetched ${patientList.length} patients for facility ${selectedFacility}`);
        setFacilityPatients(patientList);
      } catch (error) {
        console.error('Error fetching facility patients:', error);
        setPatientsError('Failed to load patients for this facility');
        setFacilityPatients([]); // Clear patients on error
      } finally {
        setLoadingPatients(false);
      }
    };

    fetchFacilityPatients();
  }, [selectedFacility, patientType]); // Add patientType as dependency

  const handleViewPatient = (patientId) => {
    navigate(`/patients/${patientId}`);
  };

  const handleSelectPatientFromTable = (patient) => {
    // Check if patient is already admitted to this facility
    if (patient.isInpatient && patient.facilityId === selectedFacility) {
      setError(`Patient ${patient.name} is already admitted to this facility. Please discharge them first before attempting a new admission.`);
      return;
    }
    
    // Clear any previous errors
    setError(null);
    setSelectedPatient(patient);
    setSelectedOncologyEpisode(''); // Reset oncology episode when patient changes
  };

  const handleClearPatientSelection = () => {
    setSelectedPatient(null);
    setSelectedOncologyEpisode(''); // Reset oncology episode when patient changes
  };

  const getAdmissionStatusChip = (status, isInpatient) => {
    if (isInpatient) {
      return <Chip label="Admitted" color="error" size="small" sx={{ fontSize: '0.75rem', height: '20px' }} />;
    }
    
    switch (status) {
      case 'admitted':
        return <Chip label="Admitted" color="error" size="small" sx={{ fontSize: '0.75rem', height: '20px' }} />;
      case 'discharged':
        return <Chip label="Discharged" color="warning" size="small" sx={{ fontSize: '0.75rem', height: '20px' }} />;
      default:
        return <Chip label="Available" color="success" size="small" sx={{ fontSize: '0.75rem', height: '20px' }} />;
    }
  };

  // Listen for facility changes and refresh admission data
  const handleFacilityChange = (newFacilityId, previousFacilityId) => {
    console.log('PatientAdmission: Facility changed, refreshing admission data');
    // Reset selected facility to trigger default facility selection
    setSelectedFacility('');
  };

  useFacilityChangeListener(handleFacilityChange);

  return (
    <Box sx={{ p: 2, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
      <HeaderCard>
        <CardContent sx={{ py: 1.5, px: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
            Patient Admission
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Admit a patient for inpatient care
          </Typography>
        </CardContent>
      </HeaderCard>
      
      <ModernCard>
        <CardContent sx={{ p: 2 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {/* Row 0: Patient Type Selection - Compact */}
              <Grid item xs={12}>
                <FormControl component="fieldset" size="small">
                  <FormLabel component="legend" sx={{ fontWeight: 600, color: 'primary.main', mb: 0.5, fontSize: '0.875rem' }}>
                    Patient Type
                  </FormLabel>
                  <RadioGroup
                    row
                    value={patientType}
                    onChange={handlePatientTypeChange}
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                  >
                    <FormControlLabel 
                      value="existing" 
                      control={<Radio size="small" />} 
                      label="Existing Patient" 
                    />
                    <FormControlLabel 
                      value="new" 
                      control={<Radio size="small" />} 
                      label="New Patient" 
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {/* Conditional Patient Form for New Patients */}
              {patientType === 'new' && (
                <Grid item xs={12}>
                  <ModernCard>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Create New Patient
                      </Typography>
                      <Box sx={{ '& > div': { maxWidth: 'none !important', margin: '0 !important' } }}>
                        <PatientForm 
                          onSuccess={handleNewPatientCreated}
                          onCancel={() => setPatientType('existing')}
                          initialFacilityId={selectedFacility}
                          embedded={true}
                          onCreationStart={handlePatientCreationStart}
                          onCreationError={handlePatientCreationError}
                        />
                      </Box>
                    </CardContent>
                  </ModernCard>
                  <Divider sx={{ my: 3 }} />
                </Grid>
              )}

              {/* Patient Selection - Compact */}
              {patientType === 'existing' && (
                <Grid item xs={12} md={4}>
                  {selectedPatient ? (
                    <Box sx={{ p: 2, border: '2px solid', borderColor: 'primary.main', borderRadius: '8px', backgroundColor: 'primary.light', color: 'white' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Selected Patient
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleClearPatientSelection}
                          sx={{ 
                            color: 'white', 
                            borderColor: 'white',
                            fontSize: '0.75rem',
                            py: 0.25,
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                              borderColor: 'white'
                            }
                          }}
                        >
                          Clear
                        </Button>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Box>
                          <Typography variant="caption" sx={{ opacity: 0.9 }}>Name</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {selectedPatient.name}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Box>
                            <Typography variant="caption" sx={{ opacity: 0.9 }}>DOB</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {selectedPatient.dateOfBirth}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ opacity: 0.9 }}>MRN</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {selectedPatient.mrn}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ p: 2, border: '2px dashed', borderColor: 'grey.300', borderRadius: '8px', textAlign: 'center' }}>
                      <PersonIcon sx={{ fontSize: 32, color: 'grey.400', mb: 1 }} />
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                        No Patient Selected
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Select from table below
                      </Typography>
                    </Box>
                  )}
                </Grid>
              )}
              {selectedPatient && patientType === 'new' && (
                <Grid item xs={12} md={4}>
                  {/* Patient info display when new patient is created */}
                  <Box sx={{ p: 2, border: '2px solid', borderColor: 'success.main', borderRadius: '8px', backgroundColor: 'success.light', color: 'white' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      New Patient Created
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Box>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>Name</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {selectedPatient.name}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box>
                          <Typography variant="caption" sx={{ opacity: 0.9 }}>DOB</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {selectedPatient.dateOfBirth}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ opacity: 0.9 }}>MRN</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {selectedPatient.mrn}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              )}

              {/* Admission Details - 2x3 Grid Layout */}
              <Grid item xs={12} md={patientType === 'new' ? 12 : 8}>
                {patientType === 'new' ? (
                  <ModernCard>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Admission Details
                      </Typography>
                      <Grid container spacing={2}>
                        {/* Row 1: Date, Facility, Admission Type */}
                        <Grid item xs={12} sm={4}>
                          <DatePicker
                            label="Admission Date"
                            value={admissionDate ? new Date(admissionDate) : null}
                            onChange={(date) => setAdmissionDate(date ? date.toISOString().split('T')[0] : '')}
                            slotProps={{
                              textField: { fullWidth: true, size: 'small', required: true }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <FormControl fullWidth>
                            <InputLabel>Facility</InputLabel>
                            <Select
                              value={selectedFacility}
                              onChange={(e) => setSelectedFacility(e.target.value)}
                              label="Facility"
                              disabled={patientType === 'new' && selectedPatient}
                            >
                              {defaultFacility && (
                                <MenuItem value={defaultFacility.id}>
                                  {defaultFacility.name} (Default)
                                </MenuItem>
                              )}
                            </Select>
                          </FormControl>
                          {patientType === 'new' && selectedPatient && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                              Facility locked for new patient
                            </Typography>
                          )}
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <CompactFormControl fullWidth required size="small">
                            <InputLabel>Admission Type</InputLabel>
                            <Select
                              value={admissionType}
                              onChange={e => setAdmissionType(e.target.value)}
                              label="Admission Type"
                            >
                              {ADMISSION_TYPES.map(type => (
                                <MenuItem key={type} value={type}>{type}</MenuItem>
                              ))}
                            </Select>
                          </CompactFormControl>
                        </Grid>
                        
                        {/* Row 2: Physician, Department, Room */}
                        <Grid item xs={12} sm={6}>
                          <CompactFormControl fullWidth required size="small">
                            <InputLabel>Attending Physician</InputLabel>
                            <Select
                              value={selectedPhysician}
                              onChange={e => setSelectedPhysician(e.target.value)}
                              label="Attending Physician"
                            >
                              {physicians.map(doc => (
                                <MenuItem key={doc.id} value={doc.id}>{doc.name || doc.email}</MenuItem>
                              ))}
                            </Select>
                          </CompactFormControl>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <CompactFormControl fullWidth required size="small">
                            <InputLabel>Department</InputLabel>
                            <Select
                              value={selectedDepartment}
                              onChange={e => setSelectedDepartment(e.target.value)}
                              label="Department"
                            >
                              {departments.map(dept => (
                                <MenuItem key={dept.id || dept.name} value={dept.id || dept.name}>{dept.name}</MenuItem>
                              ))}
                            </Select>
                          </CompactFormControl>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <CompactFormControl fullWidth required size="small">
                            <InputLabel>Room</InputLabel>
                            <Select
                              value={selectedRoom}
                              onChange={e => setSelectedRoom(e.target.value)}
                              label="Room"
                            >
                              {rooms.map(room => (
                                <MenuItem key={room.code || room.name} value={room.code || room.name}>{room.name}</MenuItem>
                              ))}
                            </Select>
                          </CompactFormControl>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </ModernCard>
                ) : (
                  <Grid container spacing={2}>
                    {/* Row 1: Date, Facility, Admission Type */}
                    <Grid item xs={12} sm={4}>
                      <DatePicker
                        label="Admission Date"
                        value={admissionDate ? new Date(admissionDate) : null}
                        onChange={(date) => setAdmissionDate(date ? date.toISOString().split('T')[0] : '')}
                        slotProps={{
                          textField: { fullWidth: true, size: 'small', required: true }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth>
                        <InputLabel>Facility</InputLabel>
                        <Select
                          value={selectedFacility}
                          onChange={(e) => setSelectedFacility(e.target.value)}
                          label="Facility"
                          disabled={patientType === 'new' && selectedPatient}
                        >
                          {defaultFacility && (
                            <MenuItem value={defaultFacility.id}>
                              {defaultFacility.name} (Default)
                            </MenuItem>
                          )}
                        </Select>
                      </FormControl>
                      {patientType === 'new' && selectedPatient && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          Facility locked for new patient
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <CompactFormControl fullWidth required size="small">
                        <InputLabel>Admission Type</InputLabel>
                        <Select
                          value={admissionType}
                          onChange={e => setAdmissionType(e.target.value)}
                          label="Admission Type"
                        >
                          {ADMISSION_TYPES.map(type => (
                            <MenuItem key={type} value={type}>{type}</MenuItem>
                          ))}
                        </Select>
                      </CompactFormControl>
                    </Grid>
                    
                    {/* Row 2: Physician, Department, Room */}
                    <Grid item xs={12} sm={6}>
                      <CompactFormControl fullWidth required size="small">
                        <InputLabel>Attending Physician</InputLabel>
                        <Select
                          value={selectedPhysician}
                          onChange={e => setSelectedPhysician(e.target.value)}
                          label="Attending Physician"
                        >
                          {physicians.map(doc => (
                            <MenuItem key={doc.id} value={doc.id}>{doc.name || doc.email}</MenuItem>
                          ))}
                        </Select>
                      </CompactFormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <CompactFormControl fullWidth required size="small">
                        <InputLabel>Department</InputLabel>
                        <Select
                          value={selectedDepartment}
                          onChange={e => setSelectedDepartment(e.target.value)}
                          label="Department"
                        >
                          {departments.map(dept => (
                            <MenuItem key={dept.id || dept.name} value={dept.id || dept.name}>{dept.name}</MenuItem>
                          ))}
                        </Select>
                      </CompactFormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <CompactFormControl fullWidth required size="small">
                        <InputLabel>Room</InputLabel>
                        <Select
                          value={selectedRoom}
                          onChange={e => setSelectedRoom(e.target.value)}
                          label="Room"
                        >
                          {rooms.map(room => (
                            <MenuItem key={room.code || room.name} value={room.code || room.name}>{room.name}</MenuItem>
                          ))}
                        </Select>
                      </CompactFormControl>
                    </Grid>
                  </Grid>
                )}
              </Grid>

              {/* Diagnosis and Notes - Compact */}
              <Grid item xs={12} md={6}>
                <CompactTextField
                  label="Admitting Diagnosis"
                  multiline
                  rows={3}
                  fullWidth
                  size="small"
                  value={admittingDiagnosis}
                  onChange={e => setAdmittingDiagnosis(e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CompactTextField
                  label="Admission Notes"
                  multiline
                  rows={3}
                  fullWidth
                  size="small"
                  value={admissionNotes}
                  onChange={e => setAdmissionNotes(e.target.value)}
                />
              </Grid>

              {/* Oncology Episode Link - Only shown if patient has active oncology episodes */}
              {selectedPatient && (
                <Grid item xs={12} md={6}>
                  <OncologyEpisodeSelector
                    patientId={selectedPatient.id}
                    facilityId={selectedFacility}
                    selectedEpisodeId={selectedOncologyEpisode}
                    onChange={setSelectedOncologyEpisode}
                    disabled={isSubmitting}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    onClick={handleOpenAllergiesModal}
                    sx={{ fontSize: '0.8rem' }}
                  >
                    {allergies.length > 0 ? `Allergies (${allergies.length})` : 'Add Allergies'}
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="small"
                    onClick={handleUploadClick}
                    disabled={!selectedPatient || isUploading}
                    startIcon={isUploading ? <CircularProgress size={14} /> : <UploadFileIcon />}
                    sx={{ fontSize: '0.8rem' }}
                  >
                    {isUploading ? 'Uploading...' : 'Upload Docs'}
                  </Button>
                  <input
                    id="document-upload-input"
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                  />
                </Box>
                {isUploading && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Uploading documents...
                    </Typography>
                    <LinearProgress />
                  </Box>
                )}
                <AllergySelectorDialog
                  open={allergiesModalOpen}
                  onSave={(selected) => { setAllergies(selected); setAllergiesModalOpen(false); }}
                  onCancel={handleCloseAllergiesModal}
                  initialAllergies={allergies}
                  patientName={selectedPatient?.name}
                />
                {allergies.length > 0 && (
                  <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {allergies.map((a, i) => (
                      <Chip key={i} label={a.name} color="error" size="small" sx={{ fontSize: '0.75rem', height: '24px' }} />
                    ))}
                  </Box>
                )}
                {uploadedDocuments.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, fontSize: '0.875rem' }}>
                      Documents ({uploadedDocuments.length})
                    </Typography>
                    <List dense sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider', py: 0 }}>
                      {uploadedDocuments.map((doc) => (
                        <ListItem
                          key={doc.id}
                          sx={{ py: 0.5 }}
                          secondaryAction={
                            <IconButton 
                              edge="end" 
                              aria-label="delete"
                              onClick={() => handleRemoveDocument(doc.id)}
                              size="small"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          }
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <AttachFileIcon color="primary" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={doc.name}
                            secondary={`${(doc.size / 1024).toFixed(1)} KB`}
                            primaryTypographyProps={{ fontSize: '0.875rem' }}
                            secondaryTypographyProps={{ fontSize: '0.75rem' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting || !selectedPatient || !selectedFacility || isCreatingPatient}
                    sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, px: 3, py: 1 }}
                  >
                    {isSubmitting ? (
                      <CircularProgress size={20} />
                    ) : isCreatingPatient ? (
                      <>
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                        Creating Patient...
                      </>
                    ) : (
                      'Admit Patient'
                    )}
                  </Button>
                  {patientType === 'new' && !selectedPatient && !isCreatingPatient && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Please create a patient using the form above before proceeding with admission.
                    </Typography>
                  )}
                </Box>
              </Grid>
              {error && <Grid item xs={12}><Alert severity="error">{error}</Alert></Grid>}
              {success && <Grid item xs={12}><Alert severity="success">{success}</Alert></Grid>}
            </Grid>
          </form>
        </CardContent>
      </ModernCard>

      {/* Facility Patients Table - Compact */}
      {selectedFacility && patientType === 'existing' && (
        <ModernCard>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PersonIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Patients at {defaultFacility?.name || 'Selected Facility'}
              </Typography>
              <Chip 
                label={`${facilityPatients.length}`} 
                color="primary" 
                size="small" 
                sx={{ ml: 1, fontSize: '0.75rem', height: '24px' }}
              />
            </Box>

            {loadingPatients ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : patientsError ? (
              <Alert severity="error" sx={{ mb: 2 }}>{patientsError}</Alert>
            ) : facilityPatients.length === 0 ? (
              <Box textAlign="center" py={4}>
                <Typography variant="body1" color="text.secondary">
                  No patients found in this facility.
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ borderRadius: '8px', maxHeight: '400px', overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', py: 1 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', py: 1 }}>MRN</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', py: 1 }}>DOB</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', py: 1 }}>Contact</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', py: 1 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', py: 1 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {facilityPatients.map((patient) => (
                      <TableRow 
                        key={patient.id}
                        sx={{ 
                          '&:hover': { backgroundColor: 'action.hover' },
                          backgroundColor: patient.isInpatient && patient.facilityId === selectedFacility
                            ? 'error.light' 
                            : selectedPatient?.id === patient.id 
                              ? 'primary.lighter' 
                              : 'inherit',
                          cursor: patient.isInpatient && patient.facilityId === selectedFacility ? 'not-allowed' : 'pointer',
                          height: '48px',
                          opacity: patient.isInpatient && patient.facilityId === selectedFacility ? 0.7 : 1,
                          '&:hover .select-patient-button': {
                            backgroundColor: 'primary.main',
                          }
                        }}
                        onClick={() => {
                          if (!(patient.isInpatient && patient.facilityId === selectedFacility)) {
                            handleSelectPatientFromTable(patient);
                          }
                        }}
                      >
                        <TableCell sx={{ fontWeight: 500, fontSize: '0.875rem', py: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PersonIcon sx={{ mr: 0.5, color: 'text.secondary', fontSize: 16 }} />
                            {patient.firstName && patient.lastName 
                              ? `${patient.firstName} ${patient.lastName}` 
                              : patient.name || 'Unknown Patient'}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.875rem', py: 1 }}>{patient.mrn}</TableCell>
                        <TableCell sx={{ fontSize: '0.875rem', py: 1 }}>{patient.dateOfBirth}</TableCell>
                        <TableCell sx={{ fontSize: '0.875rem', py: 1 }}>{patient.contact}</TableCell>
                        <TableCell sx={{ py: 1 }}>
                          {getAdmissionStatusChip(patient.admissionStatus, patient.isInpatient)}
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title={
                              patient.isInpatient && patient.facilityId === selectedFacility
                                ? "Patient already admitted to this facility"
                                : "Select for Admission"
                            }>
                              <span>
                                <IconButton
                                  className="select-patient-button"
                                  color="primary"
                                  size="small"
                                  disabled={patient.isInpatient && patient.facilityId === selectedFacility}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelectPatientFromTable(patient);
                                  }}
                                  sx={{
                                    backgroundColor: selectedPatient?.id === patient.id 
                                      ? 'primary.main' 
                                      : patient.isInpatient && patient.facilityId === selectedFacility
                                        ? 'grey.400'
                                        : 'primary.light',
                                    color: 'white',
                                    width: 28,
                                    height: 28,
                                    '&:hover': {
                                      backgroundColor: patient.isInpatient && patient.facilityId === selectedFacility
                                        ? 'grey.400'
                                        : 'primary.main',
                                    },
                                    '&:disabled': {
                                      backgroundColor: 'grey.400',
                                      color: 'grey.600',
                                    },
                                  }}
                                >
                                  <PersonIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="View Details">
                              <IconButton
                                color="secondary"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewPatient(patient.id);
                                }}
                                sx={{
                                  backgroundColor: 'secondary.light',
                                  color: 'white',
                                  width: 28,
                                  height: 28,
                                  '&:hover': {
                                    backgroundColor: 'secondary.main',
                                  },
                                }}
                              >
                                <VisibilityIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </ModernCard>
      )}
    </Box>
  );
};

// Component exported as default function above 